import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import jsQR from "jsqr";

const createDetector = () => {
  if (typeof window === "undefined") return null;
  if (!("BarcodeDetector" in window)) return null;
  try {
    return new window.BarcodeDetector({ formats: ["qr_code"] });
  } catch {
    return null;
  }
};

const normalizeText = (value) => {
  return String(value || "").trim();
};

const stopMediaStream = (stream) => {
  if (!stream) return;
  for (const track of stream.getTracks()) {
    try {
      track.stop();
    } catch {}
  }
};

const isSecureCameraContext = () => {
  if (typeof window === "undefined") return false;
  return Boolean(window.isSecureContext);
};

const canUseCamera = () => {
  if (typeof navigator === "undefined") return false;
  return Boolean(navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === "function");
};

const isIOSDevice = () => {
  if (typeof navigator === "undefined") return false;
  const ua = String(navigator.userAgent || "");
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  return Boolean(navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
};

const guessInAppBrowser = () => {
  if (typeof navigator === "undefined") return "";
  const ua = String(navigator.userAgent || "");
  if (!ua) return "";
  if (/Telegram/i.test(ua)) return "telegram";
  if (/Instagram/i.test(ua)) return "instagram";
  if (/FBAN|FBAV|FB_IAB/i.test(ua)) return "facebook";
  return "";
};

const loadImageFromFile = (file) => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("image_load_failed"));
    };
    img.src = url;
  });
};

const decodeQrFromCanvas = (canvas) => {
  if (!canvas) return null;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  const w = canvas.width || 0;
  const h = canvas.height || 0;
  if (!w || !h) return null;
  const imageData = ctx.getImageData(0, 0, w, h);
  const res = jsQR(imageData.data, w, h, { inversionAttempts: "attemptBoth" });
  return res?.data ? String(res.data) : null;
};

const QrScannerDialog = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onResult,
}) => {
  const detector = useMemo(() => createDetector(), []);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanTimerRef = useRef(null);
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);

  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [manualValue, setManualValue] = useState("");
  const inApp = useMemo(() => guessInAppBrowser(), []);
  const isIOS = useMemo(() => isIOSDevice(), []);
  const secureContext = useMemo(() => isSecureCameraContext(), []);
  const cameraSupported = useMemo(() => canUseCamera(), []);
  const canStartCamera = Boolean(secureContext && cameraSupported);
  const autoStartAllowed = Boolean(canStartCamera && !isIOS && !inApp);

  const close = () => onOpenChange?.(false);

  const cleanup = () => {
    if (scanTimerRef.current) {
      window.clearInterval(scanTimerRef.current);
      scanTimerRef.current = null;
    }
    stopMediaStream(streamRef.current);
    streamRef.current = null;
    const video = videoRef.current;
    if (video) {
      try {
        video.srcObject = null;
      } catch {}
    }
  };

  const submitResult = async (raw) => {
    const text = normalizeText(raw);
    if (!text) return;
    try {
      await Promise.resolve(onResult?.(text));
    } finally {
      close();
    }
  };

  const decodeFile = async (file) => {
    if (!file) return;
    try {
      const img = await loadImageFromFile(file);
      const maxSide = 980;
      const w0 = Number(img.naturalWidth || img.width || 0);
      const h0 = Number(img.naturalHeight || img.height || 0);
      if (!w0 || !h0) throw new Error("bad_image");
      const scale = Math.min(1, maxSide / Math.max(w0, h0));
      const w = Math.max(1, Math.round(w0 * scale));
      const h = Math.max(1, Math.round(h0 * scale));
      const canvas = canvasRef.current || document.createElement("canvas");
      canvasRef.current = canvas;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) throw new Error("no_ctx");
      ctx.drawImage(img, 0, 0, w, h);
      const decoded = decodeQrFromCanvas(canvas);
      if (!decoded) {
        setError("Не удалось распознать QR на фото");
        setStatus("fallback");
        return;
      }
      await submitResult(decoded);
    } catch {
      setError("Не удалось прочитать фото");
      setStatus("fallback");
    }
  };

  const start = async () => {
    setError(null);
    setManualValue("");
    if (!secureContext) {
      setStatus("fallback");
      return;
    }
    if (!cameraSupported) {
      setStatus("fallback");
      return;
    }
    const video = videoRef.current;
    if (!video) {
      setStatus("fallback");
      return;
    }
    setStatus("starting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });
      streamRef.current = stream;
      video.srcObject = stream;
      await video.play();
      setStatus("scanning");
      const scanKind = detector ? "detector" : "jsqr";
      scanTimerRef.current = window.setInterval(async () => {
        if (!videoRef.current) return;
        if (videoRef.current.readyState < 2) return;
        try {
          if (scanKind === "detector") {
            const results = await detector.detect(videoRef.current);
            const value = results?.[0]?.rawValue;
            if (value) await submitResult(value);
            return;
          }
          const canvas = canvasRef.current || document.createElement("canvas");
          canvasRef.current = canvas;
          const w = Math.max(1, Math.floor(videoRef.current.videoWidth || 0));
          const h = Math.max(1, Math.floor(videoRef.current.videoHeight || 0));
          if (!w || !h) return;
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d", { willReadFrequently: true });
          if (!ctx) return;
          ctx.drawImage(videoRef.current, 0, 0, w, h);
          const decoded = decodeQrFromCanvas(canvas);
          if (decoded) await submitResult(decoded);
        } catch {}
      }, 220);
    } catch (e) {
      setStatus("fallback");
      const msg =
        e?.name === "NotAllowedError"
          ? "Доступ к камере запрещён"
        : e?.name === "NotFoundError"
            ? "Камера не найдена"
            : "Не удалось запустить камеру";
      setError(msg);
    }
  };

  useEffect(() => {
    if (!open) {
      cleanup();
      setStatus("idle");
      setError(null);
      setManualValue("");
      return () => {};
    }
    setStatus("fallback");
    setError(null);
    setManualValue("");
    if (autoStartAllowed) start();
    return () => cleanup();
  }, [open]);

  return (
    <Dialog open={Boolean(open)} onOpenChange={onOpenChange}>
      <DialogContent className="text-white max-h-[85dvh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-white">{title || "Сканировать QR"}</DialogTitle>
          {description ? <DialogDescription className="text-white/70">{description}</DialogDescription> : null}
        </DialogHeader>
        <div className="space-y-3">
          {status === "scanning" || status === "starting" ? (
            <div className="rounded-xl border border-white/10 bg-black/30 overflow-hidden">
              <video ref={videoRef} className="w-full aspect-square object-cover bg-black" playsInline muted />
            </div>
          ) : (
            <div className="rounded-xl border border-white/10 bg-black/30 p-4 space-y-2">
              {error ? <div className="text-xs text-red-300">{error}</div> : null}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  const file = e?.target?.files?.[0];
                  e.target.value = "";
                  decodeFile(file);
                }}
              />
              <Button
                type="button"
                variant="outline"
                className="w-full border-white/10 bg-white/5 text-white/90 hover:bg-white/10"
                onClick={() => fileInputRef.current?.click?.()}
              >
                Сделать фото QR
              </Button>
              <div className="text-xs text-white/70">Вставьте ссылку или код из QR вручную:</div>
              <Input
                value={manualValue}
                onChange={(e) => setManualValue(e.target.value)}
                className="bg-black/10 border-white/10 text-white placeholder:text-white/40"
                placeholder="https://... или код"
              />
              <Button
                type="button"
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                onClick={() => submitResult(manualValue)}
                disabled={!normalizeText(manualValue)}
              >
                {confirmLabel || "Продолжить"}
              </Button>
            </div>
          )}
          {status === "starting" && <div className="text-xs text-white/60">Запуск камеры...</div>}
          {status === "scanning" && <div className="text-xs text-white/60">Наведите камеру на QR</div>}
          {error && (status === "starting" || status === "scanning") && <div className="text-xs text-red-300">{error}</div>}
          <Button type="button" variant="outline" className="w-full border-white/10 bg-white/5 text-white/90 hover:bg-white/10" onClick={close}>
            Закрыть
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QrScannerDialog;
